import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, AlertTriangle } from "lucide-react";

import {
  cancelEvent,
  fetchEventById,
  updateEvent,
} from "@/lib/api/events";

import { Button, LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input, Select, Textarea } from "@/components/ui/Input";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import { PageHeadline } from "@/components/ui/Headline";
import { Loader, ErrorBlock, InlineAlert } from "@/components/ui/States";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

const REVEAL_OPTIONS = [
  { value: 48, label: "2 days before event" },
  { value: 24, label: "1 day before event" },
  { value: 12, label: "12 hours before event" },
  { value: 6, label: "6 hours before event" },
  { value: 2, label: "2 hours before event (recommended)" },
];

function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventSettingsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: event, isLoading } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => fetchEventById(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const [form, setForm] = useState<{
    title: string;
    description: string;
    event_date: string;
    location: string;
    match_reveal_hours: number;
    reveal_immediately: boolean;
  } | null>(null);
  const [saved, setSaved] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifyParticipants, setNotifyParticipants] = useState(true);

  useEffect(() => {
    if (event && !form) {
      setForm({
        title: event.title,
        description: event.description ?? "",
        event_date: toLocalInputValue(event.event_date),
        location: event.location ?? "",
        match_reveal_hours: event.match_reveal_hours,
        reveal_immediately: event.reveal_immediately ?? false,
      });
    }
  }, [event, form]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!event || !form) return;
      await updateEvent(event.id, {
        title: form.title.trim(),
        description: form.description.trim(),
        event_date: new Date(form.event_date).toISOString(),
        location: form.location.trim() || null,
        match_reveal_hours: form.match_reveal_hours,
        reveal_immediately: form.reveal_immediately,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setSaved(true);
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not save"),
  });

  if (isLoading || !event) {
    return (
      <OrganizerShell>
        <Loader label="Loading event..." />
      </OrganizerShell>
    );
  }

  const update = <K extends keyof NonNullable<typeof form>>(
    key: K,
    value: NonNullable<typeof form>[K],
  ) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setSaved(false);
  };

  const retentionDate = new Date(
    new Date(event.event_date).getTime() + 30 * 86_400_000,
  ).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const dateOrLocationDirty = form
    ? form.event_date !== toLocalInputValue(event.event_date) ||
      form.location.trim() !== (event.location ?? "")
    : false;

  const revealAtMs =
    new Date(event.event_date).getTime() - event.match_reveal_hours * 3_600_000;
  const preRevealLocked = revealAtMs - Date.now() <= 6 * 3_600_000;
  const descriptionLength = form?.description.trim().length ?? 0;
  const descriptionInvalid = descriptionLength === 0;

  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[820px] animate-fade-in">
        <Link
          to={`/events/${event.id}`}
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#475569] hover:text-[#2563eb]"
        >
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>

        <div className="mt-4">
          <PageHeadline title="Event settings" subtitle={event.title} />
        </div>

        {error ? <ErrorBlock message={error} className="mt-6" /> : null}

        {form ? (
          <Card padding="md" className="mt-6">
            <h3 className="text-[16px] font-bold text-[#1a1a1a]">Event details</h3>
            <div className="mt-4 flex flex-col gap-4">
              <Field label="Event name">
                <Input
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  disabled={preRevealLocked}
                />
              </Field>
              <Field
                label="Description"
                hint={
                  preRevealLocked
                    ? "Locked — reveal is within 6 hours."
                    : "Shown to participants and used by the AI to improve match quality."
                }
              >
                <Textarea
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  rows={4}
                  disabled={preRevealLocked}
                />
              </Field>
              <Field label="Date & time">
                <DateTimePicker
                  value={form.event_date}
                  onChange={(v) => update("event_date", v)}
                />
              </Field>
              <Field label="Venue / address">
                <Input
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  placeholder="Hub Criativo do Beato, Rua do Açúcar 83, Lisbon"
                />
              </Field>
              <Field
                label="Reveal matches"
                hint={
                  form.reveal_immediately
                    ? "Matches reveal as soon as both sides like each other — no countdown."
                    : `Matches will open on ${new Date(
                        new Date(form.event_date).getTime() -
                          form.match_reveal_hours * 3_600_000,
                      ).toLocaleString("en-US", {
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}.`
                }
              >
                <Select
                  value={form.match_reveal_hours}
                  onChange={(e) => update("match_reveal_hours", Number(e.target.value))}
                  options={REVEAL_OPTIONS}
                  disabled={form.reveal_immediately}
                />
              </Field>
              <label className="flex cursor-pointer items-start gap-3 rounded-[10px] bg-[#f8fafc] p-3">
                <input
                  type="checkbox"
                  checked={form.reveal_immediately}
                  onChange={(e) => update("reveal_immediately", e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#2563eb]"
                />
                <span className="text-[13px] text-[#475569]">
                  <strong className="text-[#1a1a1a]">Reveal matches immediately.</strong>{" "}
                  Skip the countdown — names and contacts unlock the moment a match is mutual.
                </span>
              </label>

              {dateOrLocationDirty ? (
                <label className="flex cursor-pointer items-start gap-3 rounded-[10px] bg-[#f8fafc] p-3">
                  <input
                    type="checkbox"
                    checked={notifyParticipants}
                    onChange={(e) => setNotifyParticipants(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-[#2563eb]"
                  />
                  <span className="text-[13px] text-[#475569]">
                    Notify participants about the change (recommended).
                  </span>
                </label>
              ) : null}
            </div>

            <Button
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={saved || descriptionInvalid || preRevealLocked}
              className="mt-6"
              size="md"
            >
              {saveMutation.isPending ? "Saving..." : saved ? "Saved" : "Save Changes"}
            </Button>
          </Card>
        ) : null}

        <Card tone="flagged" padding="md" className="mt-5">
          <h3 className="text-[16px] font-bold text-[#92400e]">Data retention</h3>
          <p className="mt-2 text-[13px] text-[#78350f]">
            All participant videos and data are automatically deleted{" "}
            <strong>30 days after the event</strong> ({retentionDate}). Participants can delete
            their pitch any time from their profile settings.
          </p>
        </Card>

        <Card tone="danger" padding="md" className="mt-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-[#991b1b]" />
            <div className="flex-1">
              <h3 className="text-[16px] font-bold text-[#991b1b]">Danger zone</h3>
              <p className="mt-2 text-[13px] text-[#991b1b]">
                Cancel this event. Participants will be notified and pitches deleted in 7 days.
              </p>
              <LinkButton
                to={`/events/${event.id}/cancel`}
                variant="destructive"
                size="md"
                className="mt-4"
              >
                Cancel Event
              </LinkButton>
            </div>
          </div>
        </Card>

        {dateOrLocationDirty ? (
          <InlineAlert tone="info" className="mt-4">
            Date or address changed — participants will get an automated update email when you save.
          </InlineAlert>
        ) : null}

        <button type="button" onClick={() => navigate(-1)} className="hidden" />
      </div>
    </OrganizerShell>
  );
}

export function EventCancelPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => fetchEventById(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const [confirmInput, setConfirmInput] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!event) return;
      await cancelEvent(event.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      navigate("/events");
    },
  });

  if (!event) {
    return (
      <OrganizerShell>
        <Loader label="Loading event..." />
      </OrganizerShell>
    );
  }

  const matches = confirmInput.trim() === event.title;

  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[560px] animate-fade-in">
        <Card padding="lg">
          <div className="text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#fef2f2] text-3xl">
              ⚠️
            </div>
            <h2 className="mt-4 text-[24px] font-bold tracking-tight text-[#1a1a1a]">
              Cancel this event?
            </h2>
            <p className="mt-1 text-[13px] text-[#6c757d]">This action cannot be undone.</p>
          </div>

          <Card tone="danger" padding="sm" className="mt-5">
            <p className="text-[13px] font-bold text-[#991b1b]">What will happen:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-[#991b1b]">
              <li>All participants receive a cancellation email</li>
              <li>All pitch videos and profile data are deleted in 7 days</li>
              <li>Match data and analytics are permanently lost</li>
            </ul>
          </Card>

          <div className="mt-5">
            <Field label="Type the event name to confirm" hint={`Type exactly: ${event.title}`}>
              <Input
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={event.title}
              />
            </Field>
          </div>

          <div className="mt-5 flex flex-col gap-2">
            <Button
              variant="destructive"
              onClick={() => mutation.mutate()}
              disabled={!matches}
              loading={mutation.isPending}
              fullWidth
            >
              {mutation.isPending ? "Cancelling..." : "Confirm Cancellation"}
            </Button>
            <LinkButton to={`/events/${event.id}/settings`} variant="secondary" fullWidth>
              Go back
            </LinkButton>
          </div>
        </Card>
      </div>
    </OrganizerShell>
  );
}
